import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { SALVATION_PAGE_ACCESS as access } from '@/hooks/salvation/useSalvationPermissions';
import { SalvationGate } from './components/SalvationUi';

// Eager load the overview for instant access
import SalvationOverviewPage from './overview';

// Lazy load every other page
const PeoplePage = lazy(() => import('./people'));
const IntakePage = lazy(() => import('./intake'));
const TeamsPage = lazy(() => import('./teams'));
const TeamPage = lazy(() => import('./teams/TeamPage'));
const SettingsPage = lazy(() => import('./settings'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

const isId = (v: string | undefined): v is string => !!v && /^\d+$/.test(v);

/** /people/:id opens the People page with that person's drawer (links from the overview and reminders). */
function PersonRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/salvation/people" replace />;
  return (
    <SalvationGate allow={access.person}>
      <PeoplePage key={id} initialPersonId={Number(id)} />
    </SalvationGate>
  );
}

/** /teams/:id only for numeric ids; anything else goes back to the list. */
function TeamRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/salvation/teams" replace />;
  return (
    <SalvationGate allow={access.teams}>
      <TeamPage key={id} teamId={Number(id)} />
    </SalvationGate>
  );
}

/**
 * Salvation Team Routes
 *
 * Module access (Admin / SalvationAdmin / SalvationLeader, or the Salvation.Read right) is the domain gate; inside,
 * pages gate on capabilities via SalvationGate. The per-page predicates live in SALVATION_PAGE_ACCESS, which the
 * sidebar menu (salvationMenu.tsx) reads too. Leaders only ever see the campuses they lead (the API enforces it).
 *
 * /intake is the review queue for new decisions (Salvation.Intake.Review): ?tab=waiting|accepted|joined|rejected|sources
 * and ?bring=1 opens Bring in decisions. /teams/:id and /settings take ?tab= too.
 */
export default function SalvationManagementRoutes() {
  return (
    <MainLayout domainKey="salvation">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<SalvationGate allow={access.overview}><SalvationOverviewPage /></SalvationGate>} />

          {/* Follow-up */}
          <Route path="/people" element={<SalvationGate allow={access.people}><PeoplePage /></SalvationGate>} />
          <Route path="/people/:id" element={<PersonRoute />} />
          <Route path="/intake" element={<SalvationGate allow={access.intake}><IntakePage /></SalvationGate>} />

          {/* Teams */}
          <Route path="/teams" element={<SalvationGate allow={access.teams}><TeamsPage /></SalvationGate>} />
          <Route path="/teams/:id" element={<TeamRoute />} />

          {/* Administration */}
          <Route path="/settings" element={<SalvationGate allow={access.settings}><SettingsPage /></SalvationGate>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/salvation" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
